import React, { useEffect, useState } from 'react';
import styled, { CSSObject } from '@emotion/styled';

import { getIconsTheme, getPatternModal, getCompletionTheme } from '../../style';
import { connect } from 'react-redux';
import { resendEmail } from '../../actions/authActions';
import { CancelRounded, CheckCircleRounded } from '@material-ui/icons';
import { SvgIcon } from '@material-ui/core';
import ProfileCamera from '../../atoms/icons/ProfileCamera';
import ProfileEmail from '../../atoms/icons/ProfileEmail';
import ProfileInfo from '../../atoms/icons/ProfileInfo';
import { areEqual, isDarkMode, IsMobileViewPort, profileAllAboutFields, profilePercentage } from '../../utils/helpers';
import { ProgressBar } from 'swui';
import EmailConfirmV2 from '../../modules/modals/EmailConfirmV2';
import ProfilePercentDot from '../../atoms/icons/ProfilePercentDot';
import { useProfile } from '../../contexts/ProfileContext';
import { useApp } from '../../contexts/AppContext';
import { unsetBodyScrollOverflow } from '../../hooks/useDisableBodyScroll';
import useVerifyEmailWallModal from '../../hooks/useVerifyEmailWallModal';
import breakpoints from 'theme/material/breakpoints';

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const Container = styled('div')({
  padding: 0,
  margin: '0 auto',
  [breakpoints.down(768.05)]: {
    padding: 0,
    marginBottom: ({ spacedBottom }) => (spacedBottom ? 22 : 0),
  },
  [breakpoints.up(769)]: {
    marginTop: ({ screenTop }) => (screenTop ? 22 : 0),
    marginBottom: ({ screenTop }) => (screenTop ? 22 : 0),
  },
  [breakpoints.down(620.05)]: {
    marginTop: 15,
  },
});

const InfoContainer = styled('div')(() => ({
  padding: 15,
  borderRadius: 6,
  display: 'block',
  backgroundColor: isDarkMode() ? (IsMobileViewPort ? '#212121' : '#323232') : '#F6F4F4',
  border: 0,
  marginBottom: 0,
  [breakpoints.down(834.05)]: {
    marginBottom: 0,
  },
}));

const InfoHeader = styled('div')(
  (): CSSObject => ({
    fontWeight: 700,
    fontSize: 16,
    lineHeight: '20px',
    letterSpacing: '-0.3px',
    textAlign: 'center',
    maxWidth: 1070,
    margin: '0 auto',
    color: isDarkMode() ? '#71B5E4' : '#2B8FD7',
    [breakpoints.down(768.05)]: {
      fontSize: 15,
    },
  })
);

const InfoSubHeader = styled('div')`
  text-align: center;
  max-width: 1070px;
  margin: 10px auto;
  font-size: 13px;
  line-height: 18px;
  letter-spacing: -0.3px;
`;

const IconContainer = styled('div')({
  height: 40,
});

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const CheckIconContainer = styled('div')(() => ({
  position: 'absolute',
  right: '-10px',
  top: '-10px',
  background: getCompletionTheme().itemBG,
  borderRadius: '50%',
  height: 26,
  padding: 2,
}));

const InfoItemContainer = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-flow: row wrap;
  max-width: 1070px;
  margin: 0 auto;
`;

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const InfoItem = styled('div')(() => ({
  padding: '5px 0',
  width: '30%',
  background: getCompletionTheme().itemBG,
  height: 80,
  borderRadius: 6,
  margin: '1.5%',
  textAlign: 'center',
  position: 'relative',
  maxWidth: 90,
}));

const InfoItemText = styled('div')`
  font-size: 12px;
`;

const InfoItemDescription = styled('div')({
  fontSize: 12,
  fontWeight: 400,
  margin: '1px 0',
});

// @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
const PercentageLabel = styled('div')(() => ({
  fontSize: 12,
  fontWeight: 600,
  lineHeight: '19px',
  letterSpacing: 0,
  color: getPatternModal().cancelBlue,
  position: 'relative',
  height: 20,
}));

const determineColor = (status: string | null) => {
  switch (status) {
    case 'complete':
    case 'approved':
      return '#098a00';
    case 'denied':
    case 'error':
      return '#E21D4A';
    case 'not submitted':
    case 'not_started':
    default:
      return getIconsTheme().profileCompleteness;
  }
};

const commonStyles = (status: string | null) => {
  return {
    fontSize: 26,
    color: determineColor(status),
  };
};

const CompletProfileBanner = ({ onboarding }) => {
  const { footerTopPosition } = useApp();
  const { offerBoxContainerRef, calculateGalleryMediaRatio } = useProfile();
  const [openStatus, setOpenStatus] = useState(false);
  // const navigate = useNavigate();

  const { showMandatoryVerifyEmailWall } = useVerifyEmailWallModal();

  const handleToggleOpenStatus = (status: boolean | ((prevState: boolean) => boolean)) => {
    //let it be this code for now commented for now to prevent the re-rendering but not sure will it impact at other places or not, will remove later
    // const fullUrl = window.location.href.split('?');
    // const profileId = fullUrl[0].split('/').pop();
    // navigate(`/profile/${profileId}`);
    setOpenStatus(status);
    unsetBodyScrollOverflow();
  };

  const renderEmailResend = () => {
    const email = onboarding.profile && onboarding.profile.email;

    return (
      <EmailConfirmV2
        email={email}
        openStatus={openStatus}
        handleOnClick={showMandatoryVerifyEmailWall}
        handleToggleOpenStatus={handleToggleOpenStatus}
        variant={'verifyEmailWal'}
      />
    );
  };

  const renderEmailVerify = (isVerified: boolean) => {
    return (
      <InfoItem data-test-id="profile-verify-email">
        {isVerified && (
          <CheckIconContainer>
            <SvgIcon id="CheckCircleRounded" component={CheckCircleRounded} style={commonStyles('complete')} />
          </CheckIconContainer>
        )}
        <InfoItemText>
          <InfoItemDescription style={{ color: '#8f8f8f' }}>
            {isVerified ? <span>&nbsp;</span> : 'Verify Email'}
          </InfoItemDescription>
        </InfoItemText>
        <IconContainer>
          <ProfileEmail
            cssStyle={{ marginTop: isVerified ? 0 : 10 }}
            onBoarding={isVerified ? (isDarkMode() ? '#71B5E4' : '#2B8FD7') : '#8F8F8F'}
          />
        </IconContainer>
        {isVerified ? (
          <InfoItemText>
            <InfoItemDescription style={{ color: isDarkMode() ? '#71B5E4' : '#2B8FD7' }}>
              Verify Email
            </InfoItemDescription>
          </InfoItemText>
        ) : (
          renderEmailResend()
        )}
      </InfoItem>
    );
  };

  const setPhotoStatus = onboarding => {
    const { photoApproved, hasPhoto, photoFinished, profile, publicPhotos, publicVideos } = onboarding;
    const completedRequiredPublicPhotos = publicPhotos >= 1;

    switch (true) {
      case Boolean(!photoApproved && hasPhoto && publicPhotos > publicVideos && completedRequiredPublicPhotos):
        return 'pending';
      case photoApproved && publicPhotos > publicVideos:
        return 'approved';
      case photoFinished && !photoApproved && profile.photo.data.length <= 0 && profile.photo_complete:
        return 'denied';
      default:
        return null;
    }
  };

  const renderPhotoVerify = onboarding => {
    const photoStatus = setPhotoStatus(onboarding);
    const isVerified = photoStatus === 'approved';
    const isDenied = photoStatus === 'denied';

    return (
      <InfoItem data-test-id="profile-upload-photo">
        {photoStatus && (
          <CheckIconContainer>
            {photoStatus === 'denied' ? (
              <SvgIcon id="CheckCloseRounded" component={CancelRounded} style={commonStyles(photoStatus)} />
            ) : (
              <SvgIcon id="CheckCircleRounded" component={CheckCircleRounded} style={commonStyles(photoStatus)} />
            )}
          </CheckIconContainer>
        )}
        <InfoItemText>
          <InfoItemDescription>&nbsp;</InfoItemDescription>
        </InfoItemText>
        <IconContainer style={isDenied ? { height: 35 } : {}}>
          <ProfileCamera
            onBoarding={isVerified ? (isDarkMode() ? '#71B5E4' : '#2B8FD7') : determineColor(photoStatus)}
          />
        </IconContainer>
        <InfoItemText>
          <InfoItemDescription
            style={{ color: isVerified ? (isDarkMode() ? '#71B5E4' : '#2B8FD7') : determineColor(photoStatus) }}
          >
            Public Photo{isDenied ? ' Denied' : ''}
          </InfoItemDescription>
        </InfoItemText>
      </InfoItem>
    );
  };

  const renderProfileInfoVerify = (isVerified: boolean) => {
    return (
      <InfoItem data-test-id="profile-complete-info">
        {isVerified && (
          <CheckIconContainer>
            <SvgIcon id="CheckCircleRounded" component={CheckCircleRounded} style={commonStyles('complete')} />
          </CheckIconContainer>
        )}
        <InfoItemText>
          <InfoItemDescription>&nbsp;</InfoItemDescription>
        </InfoItemText>
        <IconContainer>
          <ProfileInfo onBoarding={isVerified ? (isDarkMode() ? '#71B5E4' : '#2B8FD7') : '#8F8F8F'} />
        </IconContainer>
        <InfoItemText>
          <InfoItemDescription style={{ color: isVerified ? (isDarkMode() ? '#71B5E4' : '#2B8FD7') : '#8f8f8f' }}>
            Profile Info
          </InfoItemDescription>
        </InfoItemText>
      </InfoItem>
    );
  };

  const renderPercentageCalc = () => {
    const percentageCount = profilePercentage(onboarding);

    return (
      <InfoSubHeader>
        <ProgressBar percent={percentageCount} />
        <PercentageLabel>
          <span
            style={{
              position: 'absolute',
              top: '-15px',
              left:
                percentageCount === 100 ? '95%' : `${percentageCount !== 0 ? percentageCount - 4 : percentageCount}%`,
              right: percentageCount === 100 ? '0' : 'auto',
              textAlign: 'right',
            }}
          >
            <ProfilePercentDot />
            <br />
            {percentageCount}%
          </span>
        </PercentageLabel>
      </InfoSubHeader>
    );
  };

  useEffect(() => {
    if (onboarding) {
      calculateGalleryMediaRatio();
    }
  }, [onboarding, footerTopPosition]);

  return (
    <Container data-test-id="completion-banner">
      <InfoContainer data-test-id="info-container" ref={offerBoxContainerRef}>
        <InfoHeader>Profile Completion Progress</InfoHeader>
        <InfoSubHeader>Complete profiles get seen more and get more attention!</InfoSubHeader>
        {renderPercentageCalc()}
        <InfoItemContainer data-test-id="profile-completion-banner">
          {renderEmailVerify(onboarding.emailConfirmed)}
          {renderPhotoVerify(onboarding)}
          {renderProfileInfoVerify(
            onboarding.profileFinished ||
              profileAllAboutFields({ profile: onboarding.profile, isIncludeRequiredFields: true })
          )}
        </InfoItemContainer>
      </InfoContainer>
    </Container>
  );
};

const mapStateToProps = ({ profile }) => {
  return {
    onboarding: {
      profile,
      emailConfirmed: profile && profile.email_confirmed,
      profileApproved: profile && profile.is_approved,
      profileFinished: profile && profile.required_fields_completed,
      photoFinished: profile && profile.photo_complete,
      verificationStatus: profile && profile.id_verification?.data.status,
      photoApproved:
        profile &&
        Boolean(profile.photo && profile.photo.data[0] && profile.photo.data[0].status === 'approved') &&
        profile.photo.data.filter(photo => photo.private === false).length >= 1,
      hasPhoto: profile && profile.photo && profile.photo.data.length,
      publicPhotos: profile && profile.photo && profile.photo.data.filter(photo => photo.private === false).length,
      publicVideos: profile && profile.video && profile.video.data.filter(vid => vid.private === false).length,
    },
  };
};

const mapDispatchToProps = dispatch => {
  return {
    resendReminderEmail() {
      return dispatch(resendEmail());
    },
  };
};

export default React.memo(connect(mapStateToProps, mapDispatchToProps)(CompletProfileBanner), areEqual);
